import { usePrefetch } from '@openx/components-segment/lib/hooks/usePrefetch.ts';
import { SegmentsList as SegmentListUI } from '@openx/components-segment/lib/SegmentsList/SegmentsList.tsx';
import { useSegmentContext } from '@openx/components-segment/lib/utils/context/SegmentContext.tsx';
import type { SegmentsListFilters } from '@openx/components-segment/types.ts';
import { serverSideCriteriaProcessing } from '@openx/components-table/serverSideCriteriaProcessing.ts';
import type { ListSegment } from '@openx/types/segments.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import { applySelectionChange } from '@openx/utils/lib/applySelectionChange.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import { OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config.ts';
import isEqual from 'lodash/isEqual';
import { type JSX, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { useHandleListCriteriaOnCompleted } from '../../../hooks/useHandleListCriteriaOnCompleted.ts';
import { useListCriteria } from '../../../utils/useListCriteria.ts';
import { adjustCriteriaFilters } from '../utils/adjustCriteriaFilters.ts';
import { defaultSegmentsCriteria } from '../utils/defaultSegmentsCriteria.ts';

type Props = {
  selectedSegments: ListSegment[];
  setSelectedSegments: (segments: ListSegment[]) => void;
  registerExportCriteria?: (
    criteria: AllTableCriteria<SegmentsListFilters>,
    options?: { loading?: boolean; totalCount?: number }
  ) => void;
};

export const SegmentsList = ({ selectedSegments, setSelectedSegments, registerExportCriteria }: Props): JSX.Element => {
  const { useSegmentsListFetch } = useSegmentContext();
  const { session } = useAuth();

  const { criteria, setCriteria } = useListCriteria('segments', defaultSegmentsCriteria);
  const handleCompleted = useHandleListCriteriaOnCompleted<SegmentsListFilters>(setCriteria);
  const queryFilters = useMemo(() => adjustCriteriaFilters(criteria), [criteria]);
  const queryCrit = useMemo(
    () => serverSideCriteriaProcessing({ ...criteria, filters: queryFilters }, [defaultSegmentsCriteria.sort]),
    [criteria, queryFilters]
  );
  const isOaSuborg = session?.account?.name !== OPEN_AUDIENCE_ORG_NAME;

  const { segments, error, loading, totalCount } = useSegmentsListFetch({
    options: {
      onCompleted: handleCompleted,
    },
    usePrefetch: fetchMore =>
      // biome-ignore lint/correctness/useHookAtTopLevel: need to be fixed in the future
      usePrefetch({
        fetchMore,
        pagination: criteria.pagination,
      }),
    variables: {
      ...queryCrit,
      where: {
        ...(queryCrit.where || {}),
        ...(isOaSuborg ? { account_id: { _eq: session?.account_id } } : {}),
      },
    },
  });

  useEffect(() => {
    registerExportCriteria?.(criteria, { loading, totalCount });
  }, [criteria, loading, registerExportCriteria, totalCount]);

  const handleCriteriaChange = useCallback(
    newCriteria => {
      if (
        newCriteria?.dimension === 'sort' &&
        newCriteria.value?.column === criteria.sort?.column &&
        newCriteria.value?.direction === 'asc' &&
        newCriteria.value?.column !== defaultSegmentsCriteria.sort?.column
      ) {
        newCriteria.value.column = defaultSegmentsCriteria.sort?.column;
        newCriteria.value.direction = defaultSegmentsCriteria.sort?.direction;
      }

      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);

      if (!isEqual({ ...newCriteriaProcessed }, { ...criteria })) {
        setCriteria(prev => ({ ...prev, ...newCriteriaProcessed }));
      }
    },
    [criteria, setCriteria]
  );

  const handleSelectionChange = useCallback(
    (selectedItems, operation) => {
      const selectedDealsNext = applySelectionChange({
        changeItems: selectedItems,
        itemsComparer: isEqual,
        operation,
        previouslySelectedItems: selectedSegments,
      });
      setSelectedSegments(selectedDealsNext);
    },
    [selectedSegments, setSelectedSegments]
  );

  if (error) {
    throw new Error('Segments Error');
  }

  return (
    <SegmentListUI
      criteria={criteria}
      handleCriteriaChange={handleCriteriaChange}
      handleFiltersChange={handleCriteriaChange}
      handleSelectionChange={handleSelectionChange}
      loading={loading}
      segments={loading ? [] : segments}
      selectedSegments={selectedSegments}
      setCriteria={setCriteria}
    />
  );
};
